import React from 'react'
import { useRouter } from 'next/router'
import { BenefitsTable, Faqs, Hero, ZigZag, PropValue, Quotes } from '@pcln/vip'
import { SilhouetteCard } from '@pcln/marketing'
import { ContentfulRichText, useContentful } from '@pcln/contentful-components'
import styled from 'styled-components'
import { Box, Text, Relative, Container } from 'pcln-design-system'
import EmailSignUp from '@/components/EmailSignUp'
import AppDownloadWrapper from '@/components/AppDownloadWrapper'
import { ANALYTICS_CATEGORY_VIP_LANDING } from '@/constants/analytics'
import isLoadedInAppView from '@/shared-utils/isLoadedInAppView'
import { Articles } from '@/types'
import useBootstrapData from '@/hooks/useBootstrapData'
import useDetectBrowser from '@/hooks/useDetectBrowser'
import data from './data'
import NavBar from './NavBar'
import MemberCapture from './MemberCapture'
import Intro from './Intro'
import {
  gaBenefits,
  gaFAQ,
  gaVipButton,
  gaZigZag,
  redirectToAuthPage
} from './utils'

const ContentfulRichTextStyled = styled(ContentfulRichText)`
  margin: 0px;
`

export default function LandingPage() {
  const { isMobile = false } = useBootstrapData()
  const isBrowser = useDetectBrowser()

  const handleZigZagClick = (isPageInAppView: boolean) => (id: number) => {
    gaZigZag(id)
    redirectToAuthPage({ isPageInAppView })
  }

  const FAQHeadlineFallback = 'Frequently Asked Questions'

  const articles = useContentful('articles') as Articles
  const faqs = articles?.items
    .filter(item => item?.body && item?.headline)
    .map(item => ({
      question: item.headline,
      answer: item.body && <ContentfulRichTextStyled body={item.body} />
    }))

  const { query } = useRouter()
  const isPageInAppView = isLoadedInAppView(query)

  return (
    <Box>
      <Box color="background.light">
        <Hero imageSrc={data.imageSrc} content={data.heroContent} />
        <Relative mt={[-6, null, 0]}>
          <NavBar
            categories={
              isPageInAppView
                ? Array.from(data.categories).slice(0, -1)
                : Array.from(data.categories)
            }
          />
          <Intro
            onButtonClick={() =>
              redirectToAuthPage({
                isPageInAppView
              })
            }
          />
        </Relative>
        <Box px={[2, null, null, null, 5]} pt={4} mx={[2, null, null, null, 5]}>
          <BenefitsTable id="benefits-table" navigationHandler={gaBenefits} />
        </Box>
        <Box px={3} pt={[5, null, null, null, 3]} mt="-160px">
          <Text textAlign="center" fontSize={0} color="text.light">
            *Trips are considered complete one day after the trip concludes
          </Text>
        </Box>
        <MemberCapture
          onButtonClick={() =>
            redirectToAuthPage({
              isPageInAppView
            })
          }
        />
        <PropValue {...data.valueProp} />
      </Box>
      <Box
        id="vip-family"
        size="xl"
        mt={[3, null, null, null, 5]}
        mx={[3, null, null, null, 6]}
      >
        <SilhouetteCard
          labelText="family"
          title="Unlock bigger savings 5x faster with a VIP Family account"
          firstTextLine={data.firstTextLine}
          lastTextLine={data.lastTextLine}
          benefitsList={data.familyBenefitsList}
          callToActionText="Create your VIP family"
          onClick={() => {
            gaVipButton()
          }}
        />
      </Box>
      <Box id="overview" p={3} color="background.lightest">
        <ZigZag onClick={handleZigZagClick(isPageInAppView)} {...data.zigzag} />
        <Box mx={-3} py={5}>
          {isBrowser && (
            <Quotes
              showInCarouselMode={isMobile}
              maxWidth={1086}
              contents={data.quotes}
            />
          )}
        </Box>
        <Text id="faq" mb={5} fontSize={5} textAlign="center" color="primary">
          {articles?.headline || FAQHeadlineFallback}
        </Text>
        <Box px={[2, null, null, null, 5]}>
          <Faqs onClick={gaFAQ} faqs={faqs || data.faqsFallback} />
        </Box>
      </Box>
      <Container size="xl" mb={5}>
        <EmailSignUp
          sourceId="LANDING_VIP"
          category={ANALYTICS_CATEGORY_VIP_LANDING}
        />

        {isPageInAppView ? null : (
          <Box id="app-download">
            <AppDownloadWrapper category={ANALYTICS_CATEGORY_VIP_LANDING} />
          </Box>
        )}
      </Container>
    </Box>
  )
}
